import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, X, Check } from "lucide-react";

export const WhySwitch = () => {
  const comparisons = [
    {
      traditional: "Tell you what happened.",
      hollins: "Help you plan what's next.",
    },
    {
      traditional: "Focus on tax filings and deadlines.",
      hollins: "Provide proactive accounting and year-round tax strategy.",
    },
    {
      traditional: "Hand you reports.",
      hollins: "Help you understand what the numbers mean and what to do next.",
    },
    {
      traditional: "React after problems surface.",
      hollins: "Help you stay ahead of cash flow and tax surprises.",
    },
    {
      traditional: "Offer limited strategic guidance.",
      hollins: "Provide fractional CFO support for better decisions.",
    },
    {
      traditional: "Keep the books clean.",
      hollins: "Help you build a stronger, more profitable business.",
    },
  ];

  return (
    <section className="py-10 md:py-14" style={{ backgroundColor: "hsl(40 30% 97%)" }}>
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <h2 className="font-display text-3xl md:text-4xl mb-4 text-center text-black tracking-tight">
          Why Business Owners Switch to{" "}
          <span style={{ color: "hsl(350 70% 45%)" }}>Hollins &amp; Hur</span>
        </h2>

        <p className="text-base lg:text-lg text-black/60 leading-relaxed text-center max-w-3xl mx-auto mb-4 font-bold">
          Traditional accountants help you stay compliant. Hollins &amp; Hur helps you stay compliant, make smarter decisions, and grow with more confidence.
        </p>

        <p className="text-base lg:text-lg text-black/60 leading-relaxed text-center max-w-3xl mx-auto mb-8">
          When you're running a business, clean books and filed returns are only part of the picture. You also need proactive accounting, year-round tax strategy, and fractional CFO support that helps you understand the numbers, plan ahead, and make better decisions.
        </p>

        {/* Comparison Table */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-10">
          {/* Table Header */}
          <div className="grid grid-cols-2">
            <div className="px-5 py-4 md:px-8 md:py-5 text-center" style={{
              backgroundColor: "hsl(350 70% 45%)"
            }}>
              <span className="text-white font-semibold text-sm md:text-base">Hollins &amp; Hur</span>
            </div>
            <div className="px-5 py-4 md:px-8 md:py-5 text-center" style={{
              background: "linear-gradient(135deg, hsl(0 0% 22%) 0%, hsl(0 0% 12%) 100%)"
            }}>
              <span className="text-white font-semibold text-sm md:text-base">Traditional Accountants</span>
            </div>
          </div>

          {/* Table Rows */}
          {comparisons.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-2 border-b last:border-b-0"
              style={{ borderColor: "hsl(40 20% 90%)", backgroundColor: i % 2 === 0 ? "white" : "hsl(40 30% 97%)" }}
            >
              <div className="px-4 py-4 md:px-8 md:py-5 flex items-start gap-2 md:gap-3 border-r" style={{ borderColor: "hsl(40 20% 90%)" }}>
                <div className="h-6 w-6 md:h-7 md:w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: "hsl(145 60% 92%)" }}>
                  <Check className="h-4 w-4 md:h-5 md:w-5" strokeWidth={3} style={{ color: "hsl(145 60% 30%)" }} />
                </div>
                <span className="text-sm md:text-base text-black/80 font-medium">{row.hollins}</span>
              </div>
              <div className="px-4 py-4 md:px-8 md:py-5 flex items-start gap-2 md:gap-3">
                <div className="h-6 w-6 md:h-7 md:w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: "hsl(350 90% 95%)" }}>
                  <X className="h-4 w-4 md:h-5 md:w-5" strokeWidth={3} style={{ color: "hsl(350 70% 45%)" }} />
                </div>
                <span className="text-sm md:text-base text-black/60">{row.traditional}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Closing statement */}
        <p className="text-base lg:text-lg text-black/70 leading-relaxed text-center max-w-3xl mx-auto mb-10 italic">
          We're not just here to keep your business compliant. We're here to help you move forward with more clarity, confidence, and control.
        </p>

        <div className="text-center">
          <Link to="/consultation">
            <Button
              size="lg"
              className="text-white text-lg px-10 py-6 h-auto rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: "hsl(350 70% 45%)" }}
            >
              Book a Free Discovery Call
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
